import { z } from 'zod';
import { dayFeedNudgeItemSchema } from './day-feed.js';
import { daySummarySchema } from './log.js';
import { activeAlertSchema, summaryDocumentSchema } from './summary.js';
/* ==========================================================================
 *  Chat wire contract (ISSUE-040 / ISSUE-041 / ISSUE-042 / ISSUE-043 / ISSUE-044)
 *
 *  The conversational assistant. Two hard invariants shape this contract:
 *
 *  1. READ-MOSTLY + CHEAP CONTEXT (FR-8.1/8.2, cost ceiling D-16). The model is
 *     given a COMPOSED context — profile + targets + last-7 daily summaries +
 *     current week + last month + active alerts — assembled from already-computed
 *     structured documents (ADR-0004). Raw logs are NOT in the default context;
 *     they are pulled just-in-time only when the user references a specific day.
 *
 *  2. CHAT NEVER SILENTLY WRITES (FR-8.3, J5). The model can PROPOSE writes via
 *     tool calls, but a proposal is a `pendingAction` the user must confirm. On
 *     confirm, the write routes through the SAME pipeline as every other write
 *     (extraction → validation → confidence → persistence, ADR-0002/0003; target
 *     changes reuse the never-auto-applied versioned-target path, ADR-0004 §5).
 *     The chat layer holds NO privileged write path of its own.
 *
 *  CONTRACT TYPES only — zod is the single source of truth; the web chat UI
 *  (ISSUE-044) renders these and the server composes them. The streaming/tool
 *  machinery lives server-side (see ADR-0005).
 * ========================================================================== */
/* ----- Composed context (FR-8.1) — what the assistant is given ----------- */
/** Compact profile slice the assistant needs (not the full row). */
export const chatProfileContextSchema = z.object({
    goal: z.string().nullable(),
    unitsSystem: z.enum(['metric', 'imperial']),
    restrictionsTags: z.array(z.string()).default([]),
    flaggedEd: z.boolean(), // assistant must honor ED-safe framing (ISSUE-018/058)
    /** BCP-47 locale from user.locale — used for the reply-language clause (ISSUE-125 §3). */
    locale: z.string().default('en'),
});
/**
 * The full composed context (FR-8.1). Built per request from already-computed
 * structured documents — the composer does NO LLM work and pulls NO raw logs
 * here (those are JIT, below). This is also the unit the cost story depends on:
 * bounded, summary-based, never the whole ledger.
 */
export const chatContextSchema = z.object({
    profile: chatProfileContextSchema,
    /** Active target snapshot (kcal + macros), null before first target. */
    target: z
        .object({
        kcal: z.number().int().nonnegative(),
        proteinG: z.number().int().nonnegative(),
        carbsG: z.number().int().nonnegative(),
        fatG: z.number().int().nonnegative(),
        fiberG: z.number().int().nonnegative(),
    })
        .nullable(),
    /** Last 7 DAILY summary documents (ADR-0004), most-recent first. */
    recentDays: z.array(summaryDocumentSchema).max(7).default([]),
    /** Current week summary (live), null if not yet generated. */
    currentWeek: summaryDocumentSchema.nullable().default(null),
    /** Last month rollup, null if not yet generated. */
    lastMonth: summaryDocumentSchema.nullable().default(null),
    /** Active goal-recovery / anomaly alerts (FR-7.2 / ISSUE-033). */
    activeAlerts: z.array(activeAlertSchema).default([]),
});
/**
 * `GET /me/coach/context` — the thread-preload chip payload (handoff §8:
 * `Context · Today: 1,240 kcal · P 58/140 · nudge: bridge ~20 g at snack`).
 * Class 1 (ADR-0034): composed NUMBERS + nudge parts only, never a composed
 * sentence — the client templates the chip text (same i18n invariant as the
 * day feed timeline, ADR-0032 §1). Reuses `daySummarySchema` and the
 * ADR-0033-frozen nudge shape so this is the ONE format for "today + active
 * nudge" — the same seam `ChatContextComposer` will read from once nudges
 * populate `activeAlerts` (ADR-0005 §1), not a second representation.
 */
export const coachContextResponseSchema = z.object({
    date: z.string().date(),
    totals: daySummarySchema,
    /** The same at-most-one Today nudge the day feed carries (T31.1) — null
     *  when none is active. Pre-seeds the "Ask coach" thread with zero
     *  re-explaining (design §8). */
    nudge: dayFeedNudgeItemSchema.nullable(),
});
/* ----- Citations (numeric claims trace to the ledger, J5 step 2) --------- */
/** What a chat citation points at. `entry` → a meal_entry id; `day`/`week`/
 *  `month` → a summary period; `metric` → a key inside a summary document. */
export const chatCitationTargetSchema = z.enum(['entry', 'day', 'week', 'month', 'metric']);
/** A citation marker the assistant emits; the UI renders it as an inline link
 *  (ISSUE-044 `[Mon kcal](entries/123)`). Every numeric claim should cite. */
export const chatCitationSchema = z.object({
    id: z.string(), // marker referenced from the message text, e.g. "c1"
    target: chatCitationTargetSchema,
    ref: z.string(), // entry id, ISO date, "2026-W22", "2026-05", or metric key
    label: z.string().max(120), // human link text ("Mon kcal")
});
/* ----- Clarification requests (ADR-0028 §4.2) — typed questions --------- */
/**
 * One selectable option inside a chat-level clarification request.
 * `value` is server-meaningful (e.g. a food-candidate payload or a plain id);
 * the client echoes it back verbatim when the user selects.
 *
 * Distinct from the pipeline-level `ClarificationOption` (pipeline.ts), which
 * is an in-pipeline disambiguation cue — not a user-facing form option.
 */
export const chatClarificationOptionSchema = z.object({
    label: z.string().min(1).max(200),
    value: z.unknown(),
});
/**
 * A structured question the model (or resolve pipeline) emits when it needs a
 * bounded choice before it can act — e.g. "which of these did you mean?" for
 * food disambiguation (Track 17). The client renders it as radio chips
 * (single) or checkboxes (multi); the user's selection is a NON-METERED
 * callback on POST /me/chat/actions/:clarificationId (ADR-0028 §4.2).
 *
 * Named `ChatClarificationRequest` to distinguish from the pipeline-level
 * `Clarification` type (pipeline.ts), which is an internal resolution cue.
 */
export const chatClarificationRequestSchema = z.object({
    id: z.string().uuid(),
    question: z.string().min(1).max(500),
    mode: z.enum(['single', 'multi']),
    options: z.array(chatClarificationOptionSchema).min(2),
    expiresAt: z.string().datetime(),
});
/* ----- Pending actions (FR-8.3) — proposals, never auto-executed --------- */
/** The three tools the assistant may call (ISSUE-041 AC). Each is a PROPOSAL. */
export const chatToolNameSchema = z.enum(['log_entry', 'update_target', 'request_export']);
/** `log_entry` proposal — the assistant suggests logging some natural-language
 *  text. On confirm the server runs it through the EXTRACTION PIPELINE (it does
 *  not trust the model to produce structured entries — ADR-0002/0003). */
export const logEntryProposalSchema = z.object({
    tool: z.literal('log_entry'),
    /** Natural-language log text, fed to `POST /me/logs` semantics on confirm. */
    text: z.string().min(1).max(2000),
});
/** `update_target` proposal — a full macro set + rationale. On confirm the
 *  server inserts a VERSIONED target (ISSUE-013), ED-safety re-validated
 *  server-side (ADR-0004 §5). Never auto-applied (J5 step 4). */
export const updateTargetProposalSchema = z.object({
    tool: z.literal('update_target'),
    kcal: z.number().int().positive(),
    proteinG: z.number().int().nonnegative(),
    carbsG: z.number().int().nonnegative(),
    fatG: z.number().int().nonnegative(),
    fiberG: z.number().int().nonnegative(),
    rationale: z.string().max(500),
});
/** `request_export` proposal — kicks off async GDPR-style export (ISSUE-050). */
export const requestExportProposalSchema = z.object({
    tool: z.literal('request_export'),
    scope: z.enum(['all', 'range']).default('all'),
    from: z.string().date().optional(),
    to: z.string().date().optional(),
});
export const pendingActionPayloadSchema = z.discriminatedUnion('tool', [
    logEntryProposalSchema,
    updateTargetProposalSchema,
    requestExportProposalSchema,
]);
/** A proposed action surfaced inline in the stream (ISSUE-041). The user must
 *  accept before anything executes. `id` correlates the confirm call + audit. */
export const pendingActionSchema = z.object({
    id: z.string().uuid(),
    toolCallId: z.string(), // provider tool-call id, for audit (ISSUE-042)
    summary: z.string().max(280), // human-readable "Reduce target by 150 kcal"
    payload: pendingActionPayloadSchema,
});
/* ----- Free-tier cap (FR-8.5 / ISSUE-043) -------------------------------- */
/** Daily message budget surfaced to the UI ("3 / 5 today"). Premium → unlimited
 *  (`limit` null). Returned in chat responses + a lightweight GET. */
export const chatQuotaSchema = z.object({
    used: z.number().int().nonnegative(),
    limit: z.number().int().positive().nullable(), // null = unlimited (premium)
    resetsAt: z.string().datetime(), // UTC midnight
});
/* ----- Regenerating chat battery (ADR-0024 / T10.1) ---------------------- */
/**
 * Current battery state returned by GET /me/chat-battery and embedded in
 * quota_exceeded error events. Premium users have no battery (null from the
 * route). The three honest options drive the empty-state UX in T10.4.
 */
export const chatBatteryStateSchema = z.object({
    regenCredits: z.number().int().nonnegative(),
    earnedCredits: z.number().int().nonnegative(),
    capacity: z.number().int().positive(),
    /** Seconds until the next +1 regen credit accrues. 0 when already at capacity. */
    regenEtaSeconds: z.number().int().nonnegative(),
    /** True if the user has not yet checked in today (check-in grants +2 earned). */
    canEarnCheckIn: z.boolean(),
    /** Always true for Lite users — Premium upgrade is always an option. */
    canUpgrade: z.boolean(),
});
/** Response from POST /me/check-in (ADR-0024 §4 / T10.2). */
export const checkInResponseSchema = z.object({
    /** True when the user already checked in today — no credits were added. */
    alreadyCheckedIn: z.boolean(),
    battery: chatBatteryStateSchema,
});
/* ----- SSE stream protocol (FR-8.1 streaming, first-token ≤2s) ----------- */
/**
 * One Server-Sent Event in a chat response stream. The UI assembles `token`
 * deltas, attaches `citation`s, renders `pending_action` cards, and finalizes on
 * `done` (which carries usage for the cost story / ISSUE-015). `error` ends the
 * stream cleanly so the UI can show a retry affordance.
 */
export const chatStreamEventSchema = z.discriminatedUnion('type', [
    z.object({ type: z.literal('token'), text: z.string() }),
    z.object({ type: z.literal('citation'), citation: chatCitationSchema }),
    z.object({ type: z.literal('pending_action'), action: pendingActionSchema }),
    z.object({
        type: z.literal('clarification_request'),
        clarification: chatClarificationRequestSchema,
    }),
    z.object({
        type: z.literal('done'),
        messageId: z.string().uuid(),
        usage: z
            .object({
            inputTokens: z.number().int().nonnegative(),
            outputTokens: z.number().int().nonnegative(),
            costEur: z.number().nonnegative(),
        })
            .nullable()
            .default(null),
        /** Current daily quota after this message (ISSUE-043). Null for premium users. */
        quota: chatQuotaSchema.nullable().default(null),
    }),
    z.object({
        type: z.literal('error'),
        code: z.string(),
        message: z.string(),
        /** Present on code=quota_exceeded for Lite battery (ADR-0024 §5 soft payload). */
        battery: chatBatteryStateSchema.optional(),
    }),
]);
/* ----- Shared SSE parser (ADR-0005 §Mobile M4 §M-2) ---------------------- */
/**
 * Pure, platform-agnostic SSE parser for the chat stream.
 * Buffers partial frames, splits on "\n\n", strips "data: ", JSON-parses, and
 * validates each frame against chatStreamEventSchema — dropping invalid frames
 * silently. Both web and mobile use this; neither has its own inline parser.
 *
 * Usage:
 *   const parser = new ChatStreamParser();
 *   const events = parser.push(decoder.decode(chunk, { stream: true }));
 */
export class ChatStreamParser {
    _buf = '';
    push(textChunk) {
        this._buf += textChunk;
        const frames = this._buf.split('\n\n');
        this._buf = frames.pop() ?? '';
        const events = [];
        for (const frame of frames) {
            const line = frame.trim();
            if (!line.startsWith('data: '))
                continue;
            let raw;
            try {
                raw = JSON.parse(line.slice(6));
            }
            catch {
                continue;
            }
            const result = chatStreamEventSchema.safeParse(raw);
            if (result.success)
                events.push(result.data);
        }
        return events;
    }
}
/* ----- HTTP requests ----------------------------------------------------- */
/** Body for `POST /me/chat` (SSE response). `threadId` continues a conversation;
 *  omit to start one. The server composes context; the client sends only text.
 *  `today` is the client's local calendar date (YYYY-MM-DD) so "today" resolves
 *  in the user's timezone; the server falls back to UTC when absent. */
export const chatRequestSchema = z.object({
    message: z.string().min(1).max(4000),
    threadId: z.string().uuid().optional(),
    today: z.string().date().optional(),
});
/**
 * Body for `POST /me/chat/actions/:actionId` — confirm/reject a pending
 * action, or select option(s) on a clarification request (ADR-0028).
 *
 * - `accept`/`reject` → proposal (PendingAction); unchanged from before.
 * - `select` → clarification_request; `selected` carries the chosen option
 *   value(s) echoed back from the rendered form.  Zero battery charged for
 *   all three — they are commands, not messages (ADR-0028 §3).
 */
export const confirmActionSchema = z.discriminatedUnion('decision', [
    z.object({ decision: z.literal('accept') }),
    z.object({ decision: z.literal('reject') }),
    z.object({ decision: z.literal('select'), selected: z.array(z.unknown()).min(1) }),
]);
/** Result of confirming an action or resolving a clarification. */
export const actionResultSchema = z.object({
    actionId: z.string().uuid(),
    decision: z.enum(['accept', 'reject', 'select']),
    executed: z.boolean(),
    result: z.record(z.string(), z.string()).nullable().default(null),
});
/* ----- Persisted message view (audit + threads, ISSUE-042/044) ----------- */
export const chatRoleSchema = z.enum(['user', 'assistant']);
/** A stored chat message (audit log is NOT the data of record — FR-8.4). */
export const chatMessageViewSchema = z.object({
    id: z.string().uuid(),
    threadId: z.string().uuid(),
    role: chatRoleSchema,
    content: z.string(),
    citations: z.array(chatCitationSchema).default([]),
    pendingActions: z.array(pendingActionSchema).default([]),
    /** Clarification requests attached to this message (ADR-0028 §4.2). */
    clarifications: z.array(chatClarificationRequestSchema).default([]),
    /** Historical actions are render-only; the live action store is not re-armed. */
    actionsAreLive: z.boolean().default(true),
    createdAt: z.string().datetime(),
});
export const chatThreadSummarySchema = z.object({
    threadId: z.string().uuid(),
    title: z.string().min(1).max(80),
    lastMessageAt: z.string().datetime(),
    messageCount: z.number().int().nonnegative(),
});
export const chatThreadsResponseSchema = z.object({
    threads: z.array(chatThreadSummarySchema),
    nextCursor: z.string().nullable(),
});
export const chatThreadMessagesResponseSchema = z.object({
    threadId: z.string().uuid(),
    messages: z.array(chatMessageViewSchema.extend({ actionsAreLive: z.literal(false) })),
});
//# sourceMappingURL=chat.js.map